import React, { useState } from 'react';
import { SEO } from '../components/SEO';
import { Calendar, ArrowRight } from 'lucide-react';
import SectionHeader from '../components/SectionHeader';
import { Link } from 'react-router-dom';

export const KnowledgeHub = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  const categories = ['All', 'Regulatory Affairs', 'CDSCO', 'FDA', 'GMP', 'Facility Design', 'Drug Sourcing', 'Industry News'];

  const articles = [
    {
      title: 'Understanding India\'s Revised Schedule M: Key Changes and What Manufacturers Must Do Now',
      excerpt: 'A comprehensive guide to the updated GMP requirements under India\'s Revised Schedule M and actionable steps for pharmaceutical manufacturers to achieve compliance.',
      category: 'GMP',
      date: 'March 15, 2025',
      image: 'https://images.unsplash.com/photo-1705727210721-961cc64a6895?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    },
    {
      title: 'CDSCO Medical Device Registration: Step-by-Step Guide for Class B Devices',
      excerpt: 'Navigate the CDSCO medical device registration process with our detailed walkthrough covering classification, documentation, and submission requirements.',
      category: 'CDSCO',
      date: 'March 10, 2025',
      image: 'https://images.pexels.com/photos/11703173/pexels-photo-11703173.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    },
    {
      title: 'How to Prepare for an FDA Pre-Approval Inspection: A Practical Checklist',
      excerpt: 'Essential preparation steps and best practices to ensure your facility is ready for FDA Pre-Approval Inspections (PAI) and increase approval likelihood.',
      category: 'FDA',
      date: 'March 5, 2025',
      image: 'https://images.pexels.com/photos/19471014/pexels-photo-19471014.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    },
    {
      title: 'Reference Listed Drugs (RLDs): What They Are and How to Source Them for BA/BE Studies',
      excerpt: 'Everything you need to know about RLDs, their importance in bioequivalence studies, and how to procure them from regulated markets efficiently.',
      category: 'Drug Sourcing',
      date: 'February 28, 2025',
      image: 'https://images.unsplash.com/photo-1742189406399-a683d5650e06?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    },
    {
      title: 'Cleanroom Classification: ISO Standards vs Schedule M vs EU GMP - A Comparison',
      excerpt: 'A detailed comparison of cleanroom classification systems across ISO, Indian Schedule M, and EU GMP Annex 1 to help you design compliant facilities.',
      category: 'Facility Design',
      date: 'February 20, 2025',
      image: 'https://images.unsplash.com/photo-1669101283985-ab2b293923df?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    },
    {
      title: 'FSSAI vs CDSCO: Which Authority Regulates Your Nutraceutical Product?',
      excerpt: 'Clear guidance on regulatory jurisdiction for nutraceutical products in India, including classification criteria and compliance requirements.',
      category: 'Regulatory Affairs',
      date: 'February 15, 2025',
      image: 'https://images.unsplash.com/photo-1576669801838-1b1c52121e6a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    },
    {
      title: 'Top 5 GMP Non-Compliance Observations in CDSCO Facility Inspections - And How to Avoid Them',
      excerpt: 'Learn from common inspection failures and implement proactive measures to ensure your facility passes CDSCO GMP audits successfully.',
      category: 'GMP',
      date: 'February 10, 2025',
      image: 'https://images.unsplash.com/photo-1705727210721-961cc64a6895?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    },
    {
      title: 'CTD Dossier Preparation: Common Mistakes That Lead to Query Letters',
      excerpt: 'Avoid regulatory delays by understanding the most frequent errors in CTD dossier preparation and how to ensure submission-ready quality.',
      category: 'Regulatory Affairs',
      date: 'February 1, 2025',
      image: 'https://images.pexels.com/photos/19471014/pexels-photo-19471014.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    },
  ];

  const filteredArticles = activeFilter === 'All'
    ? articles
    : articles.filter(article => article.category === activeFilter);

  return (
    <div className="pt-[72px]">
      <SEO page="knowledge-hub" />
      {/* Hero */}
      <section
        className="relative py-24"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1576669801838-1b1c52121e6a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0" style={{ background: 'rgba(255, 255, 255, 0.75)' }} />
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" style={{ color: '#1B3A6B' }}>
            Knowledge Hub & Regulatory Insights
          </h1>
          <p className="text-xl max-w-3xl mx-auto" style={{ color: '#1E293B' }}>
            Expert perspectives on regulatory affairs, compliance, and the life sciences industry - from the Pegasus team.
          </p>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-24 bg-white" data-testid="blog-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          {/* Filter Bar */}
          <div className="mb-12">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  data-testid={`filter-${category}`}
                  onClick={() => setActiveFilter(category)}
                  className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                    activeFilter === category
                      ? 'bg-[#1570EF] text-white'
                      : 'bg-[#EFF6FF] text-[#1B3A6B] border border-[#BFDBFE] hover:border-[#1570EF]'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Articles Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, index) => (
              <div
                key={index}
                data-testid={`article-card-${index}`}
                className="bg-white rounded-2xl border border-[#BFDBFE] overflow-hidden hover:border-[#1570EF] hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="inline-block px-3 py-1 bg-[#1570EF] text-white text-xs font-semibold rounded-full mb-3">
                    {article.category}
                  </div>
                  <h3 className="text-lg font-bold mb-3 line-clamp-2" style={{ color: '#1B3A6B' }}>
                    {article.title}
                  </h3>
                  <p className="text-sm mb-4 line-clamp-3" style={{ color: '#1E293B' }}>
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs" style={{ color: '#64748B' }}>
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      <span>{article.date}</span>
                    </div>
                    <button className="inline-flex items-center gap-1 text-[#1570EF] font-semibold hover:gap-2 transition-all duration-200">
                      Read More
                      <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Note */}
          <div className="mt-12 text-center" style={{ color: '#64748B' }}>
            <p className="text-sm">
              Stay ahead with Pegasus Clinicare's regulatory knowledge hub. We publish new insights regularly to help you navigate the evolving compliance landscape.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KnowledgeHub;
